import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { cacheable, Cache, DefaultCacheSerializer } from '../../cache/index';
import { oneMinuteMilli } from '../../constants-types/constants';
import { GlobalEmailTypes } from '../../constants-types/Enums';
import { PI } from '../../constants-types/PI.model';
import { Language, FullSiteLanguage } from '../../shared/Language.model';
import { ConfigurationService } from '../../system-configuration/Configuration.service';
import { ILanguagesApi } from './LanguagesApi.connectinterface';

@Injectable({
    providedIn: 'root'
})
export class LanguagesApi implements ILanguagesApi {
    currentLocale: string;

    emailSubjectMessageOverridesCache = new Cache<
        any,
        {
            keysToTranslate: string[];
            languageCode: string;
            companyId: string;
            emailType: GlobalEmailTypes;
        }
    >(
        'emailSubjectMessageOverridesCache',
        (args: {
            keysToTranslate: string[];
            languageCode: string;
            companyId: string;
            emailType: GlobalEmailTypes;
        }) =>
            JSON.stringify(
                this.getEmailSubjectMessageOverridesParams(
                    args.keysToTranslate,
                    args.languageCode,
                    args.companyId,
                    args.emailType
                )
            )
    );

    constructor(
        private http: HttpClient,
        private config: ConfigurationService
    ) {
        this.currentLocale = PI.languageLocale;
    }

    getJobAssessmentLanguages(): Promise<Language[]> {
        return Promise.resolve([]);
    }
    fillInKeys(
        keysToTranslate: string[],
        languageCode: string
    ): Promise<any[]> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Accept: 'application/json'
        });
        const params: {
            [key: string]: string | string[];
        } = {};
        params.keySet = keysToTranslate.join(',');
        params.currentLanguageLocale = languageCode;
        const options = {
            headers,
            params
        };
        return firstValueFrom(
            this.http.get<any[]>(
                this.config.getClassicURL() +
                    '/api/v1/Translations/InternalConsumption/',
                options
            )
        );
    }

    @cacheable(
        function (this: LanguagesApi) {
            return this.emailSubjectMessageOverridesCache;
        },
        (
            keysToTranslate: string[],
            languageCode: string,
            companyId: string,
            emailType: GlobalEmailTypes
        ) => ({
            keysToTranslate,
            languageCode,
            companyId,
            emailType
        }),
        null,
        new DefaultCacheSerializer(),
        5 * oneMinuteMilli
    )
    getEmailSubjectMessageOverrides(
        keysToTranslate: string[],
        languageCode: string,
        companyId: string,
        emailType: GlobalEmailTypes
    ): Promise<any[]> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Accept: 'application/json'
        });
        const params = this.getEmailSubjectMessageOverridesParams(
            keysToTranslate,
            languageCode,
            companyId,
            emailType
        );
        const options = {
            headers,
            params
        };
        return firstValueFrom(
            this.http.get<any[]>(
                this.config.getClassicURL() +
                    '/api/v1/Translations/EmailSubjectMessage',
                options
            )
        );
    }

    getLanguageList(): Promise<FullSiteLanguage[]> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Accept: 'application/json'
        });
        const options = {
            headers
        };
        return firstValueFrom(
            this.http.get<FullSiteLanguage[]>(
                this.config
                    .getAlternateAPIs()
                    .filter((alt) => alt.altPath.includes('api/v4'))[0].altUrl +
                    '/api/v4/languages',
                options
            )
        );
    }

    getPI2Languages(): Observable<FullSiteLanguage[]> {
        return this.http
            .get<
                FullSiteLanguage[]
            >(this.config.getAlternateAPIs().filter((alt) => alt.altPath.includes('api/v4'))[0].altUrl + '/api/v4/languages')
            .pipe(
                map((fullSiteLanguages) =>
                    fullSiteLanguages.filter((lang) =>
                        PI.productLanguageMap.FullSite.filter(
                            (x) => x.isAvailableForPI2
                        ).find((x) => x.languageLocaleCode === lang.localeCode)
                    )
                )
            );
    }

    private getEmailSubjectMessageOverridesParams(
        keysToTranslate: string[],
        languageCode: string,
        companyId: string,
        emailType: GlobalEmailTypes
    ) {
        return {
            keySet: keysToTranslate.join(','),
            currentLanguageLocale: languageCode,
            companyId: companyId,
            emailType: emailType.toString()
        };
    }
}
